import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, getDaysLeft, getExpirationInfo } from '../../../../lib/date/formatDate';
import { DATE_FORMAT } from '../../../../lib/resolveFields';
import type { RegistrationKey, RegistrationKeyStatus } from '../../instanceTypes';
import { generateButtonSx, KEY_CARD } from './generateButtonSx';

interface KeyCardProps {
  lastKey: RegistrationKey;
  revoking: boolean;
  generating: boolean;
  /** Hides revoke and generate (ADR-040 D5). */
  readOnly?: boolean;
  onRevoke: (key: RegistrationKey) => void;
  onGenerate: () => void;
}

const STATUS_LABELS: Record<RegistrationKeyStatus, string> = {
  ACTIVE: 'keys.statusLabels.ACTIVE',
  CONSUMED: 'keys.statusLabels.CONSUMED',
  EXPIRED: 'keys.statusLabels.EXPIRED',
  REVOKED: 'keys.statusLabels.REVOKED',
};

export function KeyCard({
  lastKey,
  revoking,
  generating,
  readOnly = false,
  onRevoke,
  onGenerate,
}: KeyCardProps) {
  const { t, i18n } = useTranslation('instances');
  const theme = useTheme();
  const cyan = theme.navigation.main.selectedAccent;

  const statusStyle = useMemo(
    () =>
      ({
        ACTIVE: {
          dot: theme.palette.success.main,
          text: theme.palette.success.dark,
          glow: alpha(theme.palette.success.main, 0.16),
        },
        CONSUMED: {
          dot: theme.palette.text.disabled,
          text: theme.palette.grey[600],
          glow: alpha(theme.palette.grey[500], 0.16),
        },
        EXPIRED: {
          dot: theme.palette.warning.main,
          text: theme.palette.warning.dark,
          glow: alpha(theme.palette.warning.main, 0.18),
        },
        REVOKED: {
          dot: theme.palette.error.main,
          text: theme.palette.error.dark,
          glow: alpha(theme.palette.error.main, 0.16),
        },
      })[lastKey.status],
    [lastKey.status, theme],
  );

  const meta = useMemo(() => {
    switch (lastKey.status) {
      case 'ACTIVE': {
        const daysLeft = getDaysLeft(lastKey.expiresAt);
        return {
          label: t('keys.metaExpiresOn'),
          date: formatDate(lastKey.expiresAt, DATE_FORMAT),
          extra:
            daysLeft > 0
              ? { text: t('keys.expiresInDays', { count: daysLeft }), warn: true }
              : undefined,
        };
      }
      case 'CONSUMED':
        return {
          label: t('keys.metaUsedOn'),
          date: lastKey.usedAt ? formatDate(lastKey.usedAt, DATE_FORMAT) : '—',
          extra: undefined,
        };
      case 'EXPIRED': {
        const { daysAgo } = getExpirationInfo(lastKey.expiresAt);
        return {
          label: t('keys.metaExpiredOn'),
          date: formatDate(lastKey.expiresAt, DATE_FORMAT),
          extra: { text: t('keys.daysAgo', { count: daysAgo, lng: i18n.language }), warn: false },
        };
      }
      case 'REVOKED':
        return {
          label: t('keys.metaRevokedOn'),
          date: lastKey.revokedAt ? formatDate(lastKey.revokedAt, DATE_FORMAT) : '—',
          extra: undefined,
        };
    }
  }, [lastKey, t, i18n.language]);

  const showGenerate = !readOnly && (lastKey.status === 'EXPIRED' || lastKey.status === 'REVOKED');

  const footTextKey =
    lastKey.status === 'CONSUMED'
      ? 'keys.footText.consumed'
      : lastKey.status === 'EXPIRED'
        ? 'keys.footText.expired'
        : lastKey.status === 'REVOKED'
          ? 'keys.footText.revoked'
          : null;

  return (
    <Box
      component="article"
      aria-label={`${t('detail.section.keys')} — ${lastKey.hint}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: KEY_CARD.cardGap,
          px: KEY_CARD.cardPx,
          py: KEY_CARD.cardPy,
        }}
      >
        <VpnKeyIcon
          aria-hidden="true"
          sx={{ fontSize: theme.iconSize.sm, color: cyan, flexShrink: 0 }}
        />

        <Typography
          sx={{
            fontSize: theme.typography.fontSizeDescription,
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.text.label,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {t('detail.section.keys')}
        </Typography>

        <Box
          aria-hidden="true"
          sx={{
            width: '1px',
            height: theme.iconSize.xs,
            bgcolor: alpha(theme.palette.common.black, 0.12),
            flexShrink: 0,
            alignSelf: 'center',
          }}
        />

        <Typography
          sx={{
            fontSize: theme.typography.body1.fontSize,
            fontWeight: theme.typography.fontWeightSemiBold,
            color: theme.palette.text.body,
            fontFamily: 'monospace',
            letterSpacing: '0.01em',
            flexShrink: 0,
          }}
        >
          {lastKey.hint}…
        </Typography>

        <Box sx={{ flex: 1, minWidth: KEY_CARD.spacerMinWidth }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: KEY_CARD.metaGap,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: theme.typography.fontSizeDescription, color: theme.palette.text.label }}
          >
            {meta.label}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: theme.typography.fontSizeDescription,
              color: theme.palette.text.body,
              fontWeight: theme.typography.fontWeightSemiBold,
            }}
          >
            {meta.date}
          </Typography>
          {meta.extra && (
            <Typography
              component="span"
              sx={{
                fontSize: theme.typography.fontSizeDescription,
                fontWeight: theme.typography.fontWeightSemiBold,
                color: meta.extra.warn ? theme.palette.warning.dark : theme.palette.text.label,
              }}
            >
              {meta.extra.text}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: KEY_CARD.statusGap,
            fontSize: theme.typography.fontSizeDescription,
            fontWeight: theme.typography.fontWeightSemiBold,
            color: statusStyle.text,
            flexShrink: 0,
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: KEY_CARD.dotSize,
              height: KEY_CARD.dotSize,
              borderRadius: theme.borderRadius.pill,
              bgcolor: statusStyle.dot,
              boxShadow: `0 0 0 ${KEY_CARD.dotGlowRadius}px ${statusStyle.glow}`,
              flexShrink: 0,
            }}
          />
          {t(STATUS_LABELS[lastKey.status])}
        </Box>

        {!readOnly && lastKey.status === 'ACTIVE' && (
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={revoking}
            aria-label={`${t('keys.revokeButton')} ${lastKey.hint}`}
            aria-busy={revoking}
            startIcon={
              revoking ? (
                <CircularProgress size={theme.iconSize.xs} color="inherit" />
              ) : (
                <CloseIcon />
              )
            }
            onClick={() => onRevoke(lastKey)}
            sx={{
              ...generateButtonSx(theme),
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: theme.palette.error.dark,
                boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.15)}`,
              },
              '&:disabled': {
                opacity: 0.55,
                bgcolor: theme.palette.error.main,
                color: theme.palette.common.white,
              },
            }}
          >
            {t('keys.revokeButton')}
          </Button>
        )}

        {showGenerate && (
          <Button
            size="small"
            variant="contained"
            disabled={generating}
            aria-busy={generating}
            startIcon={
              generating ? (
                <CircularProgress size={theme.iconSize.xs} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            onClick={onGenerate}
            sx={generateButtonSx(theme)}
          >
            {generating ? t('actions.generatingKey') : t('actions.generateKey')}
          </Button>
        )}
      </Box>

      {footTextKey && (
        <Typography
          variant="body2"
          sx={{ px: KEY_CARD.cardPx, pb: KEY_CARD.cardPb, color: theme.palette.text.label }}
        >
          {t(footTextKey)}
        </Typography>
      )}
    </Box>
  );
}
